import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import {useSelector} from 'react-redux';
import {LayoutSelector, PagesSelector, UserAssetSelector} from '../../../Selectors';
import {useExperience} from '../../Experience/ExperienceProvider';
import useSettings from '../../../Hooks/useSettings';
import {
  AIMode,
  AssetType,
  AssistantType,
  BoxLayer,
  BoxType,
  Currency,
  CurrencyAdapter,
  NumBool,
  PageConstants,
} from '@taopix/taopix-core';
import {
  ActionType,
  ExperienceType,
  PageInterface,
  PageItem,
  ProductLayout,
} from '@taopix/taopix-core/dist/types/types';
import {AssistantActions, AssistantActionTypes} from './AssistantActionTypes';
import {useTaopix} from '../TaopixContext';
import {PhotoBookPricingEngine} from '@taopix/taopix-core/dist/types/PricingEngine/PhotoBookPricingEngine';
import {useUserFlowState} from '../UserFlow/UserFlowContext';
import {UserFlowDirection, UserFlowStep} from '../../../Facade/UserFlow/BaseUserFlow';
import {UserFlowType} from '../../../Facade/UserFlow/UserFlowFacade';
import {CoverMode} from '../../Assistant/Step/CreateCoverStep';
import {ROUTE_ASSISTANT} from '../../../Constants';

export type AssistantAction = ActionType<typeof import('./AssistantActionTypes')>;

export type AssistantState = {
  step: string;
  historyID: number;
  productLayout: ProductLayout;
  runCount: number;
  settings: {
    addPicturesStep: {
      userAssets: any;
    };
    cover1Step: {
      pictures: number[];
      text: PageItem[];
    };
    cover2Step: {
      pictures: number[];
      text: PageItem[];
    };
    pageCountModeStep: {
      minPages: number;
      maxPages: number;
      defaultPages: number;
      step: number;
    };
  };
  pages: PageInterface[];
  coverAssets: {
    [CoverMode.Front]: number[];
    [CoverMode.Back]: number[];
  };
  selectedAssets: number[];
  coverItems: {
    front: {
      hasCover: boolean;
      textBox: PageItem[];
      pictureBox: Record<number, number>[];
    };
    back: {
      hasCover: boolean;
      textBox: PageItem[];
      pictureBox: Record<number, number>[];
    };
  };
  useAllPictures: boolean;
  changeDetected: boolean;
  setPageCount: boolean;
  pageCount: number;
  originalPageCount: number;
  children?: any;
};

export type AssistantContextState = {
  state: AssistantState;
  dispatch: React.Dispatch<AssistantAction>;
  getPricingEngine: (AiMode: AIMode) => PhotoBookPricingEngine;
  formatCoverItems: () => any;
  currency: Currency;
};

const initialState: AssistantState = {
  step: '',
  historyID: 0,
  runCount: 0,
  productLayout: {} as ProductLayout,
  settings: {
    addPicturesStep: {
      userAssets: [],
    },
    cover1Step: {
      pictures: [],
      text: [],
    },
    cover2Step: {
      pictures: [],
      text: [],
    },
    pageCountModeStep: {
      minPages: 0,
      maxPages: 0,
      defaultPages: 0,
      step: 1,
    },
  },
  pages: [],
  coverAssets: {
    [CoverMode.Front]: [],
    [CoverMode.Back]: [],
  },
  selectedAssets: [],
  coverItems: {
    front: {
      hasCover: false,
      textBox: [],
      pictureBox: [],
    },
    back: {
      hasCover: false,
      textBox: [],
      pictureBox: [],
    },
  },
  useAllPictures: false,
  changeDetected: false,
  setPageCount: false,
  pageCount: -1,
  originalPageCount: -1,
};

let cachedState: Partial<AssistantState> = {};

export const AssistantContext = createContext<AssistantContextState>({} as AssistantContextState);

const assistantReducer = (state: AssistantState, action: AssistantAction): AssistantState => {
  switch (action.type) {
    case AssistantActionTypes.SET_INIT: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case AssistantActionTypes.SET_SETTINGS: {
      return {
        ...state,
        settings: {
          ...state.settings,
          ...action.payload,
        },
      };
    }
    case AssistantActionTypes.SET_ASSETS: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case AssistantActionTypes.ADD_COVER_ASSETS: {
      return {
        ...state,
        coverAssets: {
          ...state.coverAssets,
          [action.payload.coverType]: [
            ...new Set([...state.coverAssets[action.payload.coverType], ...action.payload.assets]),
          ],
        },
      };
    }
    case AssistantActionTypes.SET_COVER_ASSETS: {
      return {
        ...state,
        coverAssets: {
          ...state.coverAssets,
          [action.payload.coverType]: [...new Set([...action.payload.assets])],
        },
      };
    }
    case AssistantActionTypes.REMOVE_COVER_ASSETS: {
      return {
        ...state,
        coverAssets: {
          ...state.coverAssets,
          [action.payload.coverType]: state.coverAssets[action.payload.coverType].filter(
            asset => !action.payload.assets.includes(asset)
          ),
        },
      };
    }
    case AssistantActionTypes.SET_SELECTED_ASSETS: {
      const mergeList = action.payload.clear ? [] : [...state.selectedAssets];
      return {
        ...state,
        selectedAssets: [...new Set([...mergeList, ...action.payload.assets])],
      };
    }
    case AssistantActionTypes.REMOVE_SELECTED_ASSETS: {
      return {
        ...state,
        selectedAssets: state.selectedAssets.filter(asset => !action.payload.includes(asset)),
      };
    }
    case AssistantActionTypes.UPDATE_COVER_PAGE_ITEMS: {
      const newState = {...state};

      if (action.payload.key === 'textBox') {
        action.payload.pageItems.forEach(pageItem => {
          const index = newState.coverItems[action.payload.coverMode]['textBox'].findIndex(
            oldPageItem => oldPageItem.ref === pageItem.ref
          );

          if (index > -1) {
            newState.coverItems[action.payload.coverMode][action.payload.key][index] = pageItem;
          }
        });
      } else {
        action.payload.pageItems.forEach(pageItem => {
          const index = newState.coverItems[action.payload.coverMode]['pictureBox'].findIndex(
            oldPageItem => oldPageItem === pageItem
          );

          if (index > -1) {
            newState.coverItems[action.payload.coverMode][action.payload.key][index] = pageItem;
          }
        });
      }

      return newState;
    }
    case AssistantActionTypes.SET_USE_ALL_PICTURES: {
      return {
        ...state,
        useAllPictures: action.payload,
      };
    }
    case AssistantActionTypes.SET_PAGE_COUNT: {
      return {
        ...state,
        setPageCount: action.payload.hasSet,
        pageCount: action.payload.pageCount,
      };
    }
    case AssistantActionTypes.SET_HISTORYID: {
      return {
        ...state,
        historyID: action.payload,
      };
    }
    case AssistantActionTypes.INCREMENT_RUN_COUNT: {
      return {
        ...state,
        runCount: ++state.runCount,
      };
    }
    case AssistantActionTypes.SET_CHANGE_DETECTED: {
      return {
        ...state,
        changeDetected: action.payload,
      };
    }
    default:
      return state;
  }
};

const AssistantProvider = ({children}: {children: any}) => {
  const taopix = useTaopix();
  const settings = useSettings();
  const userFlowState = useUserFlowState();
  const experience = useExperience();

  const mounted = useRef<boolean>(!window.location.hash.includes(ROUTE_ASSISTANT));

  const pages = useSelector(PagesSelector);
  const assets = useSelector(UserAssetSelector);
  const productLayout = useSelector(LayoutSelector);
  const currentStep = userFlowState.getCurrentStep(UserFlowType.Assistant);
  const deepClone = taopix.core.get('utils').deepClone;

  const currency = useMemo(
    () => CurrencyAdapter(settings.state.currency),
    [settings.state.currency.code]
  );

  const [state, dispatch] = useReducer(assistantReducer, {
    ...initialState,
  });

  const getPageCount = (pageCount: number): number => {
    // Smart Design works with even number pages
    // so we need for force the page count to be even.
    if (
      AssistantType.SmartDesign ===
      experience.experience.WIZARD.designAssistantType.designAssistantType
    ) {
      return 2 * Math.ceil(pageCount / 2);
    }

    return pageCount;
  };

  const onAssistantLoadCallback = useCallback(payload => {
    document.title = payload.designerSettings.project.name;

    if (mounted.current) return;

    // Get experience directly as it may not be updated in the provider yet.
    const experience = taopix.core.get('experience') as unknown as ExperienceType;

    const restoredState = deepClone({...state});

    restoredState.productLayout = payload.designerSettings.project.layout;

    // Set initial settings based on the experience.
    restoredState.coverItems.front = {
      hasCover: experience.WIZARD.cover1Step.allowCustomersToSelectPicturesAndText,
      textBox: Object.values(payload.assistantSettings.cover1Step.text),
      pictureBox: payload.assistantSettings.cover1Step.pictures.map(pictureRef => {
        return {[pictureRef]: -1};
      }),
    };
    restoredState.coverItems.back = {
      hasCover: experience.WIZARD.backCoverStep.allowCustomersToSelectPicturesAndText,
      textBox: Object.values(payload.assistantSettings.backCoverStep.text),
      pictureBox: payload.assistantSettings.backCoverStep.pictures.map(pictureRef => {
        return {[pictureRef]: -1};
      }),
    };

    restoredState.useAllPictures = 'uap' === experience.WIZARD.picSelectionModeStep.defaultMode;

    const sessionStorage = window.Taopix.Core.get('states').sessionStorage;

    // Load session storage data, but only set if experience allows it.
    const sessionStorageData = JSON.parse(deepClone(sessionStorage.getItem('assistant')));

    if (sessionStorageData) {
      if (sessionStorageData.token === payload.designerSettings.tokens.token) {
        cachedState = deepClone(sessionStorageData);
      }

      if (Object.keys(cachedState).length > 0) {
        // Restore history ID.
        restoredState.historyID = cachedState.historyID || 0;

        if (cachedState.coverAssets) {
          restoredState.coverAssets = cachedState.coverAssets;
        }

        if (
          cachedState.useAllPictures &&
          'isp' !== experience.WIZARD.picSelectionModeStep.defaultMode
        ) {
          restoredState.useAllPictures = cachedState.useAllPictures;
        }

        if (cachedState.setPageCount && 'ccc' === experience.WIZARD.pageCountModeStep.defaultMode) {
          restoredState.setPageCount = cachedState.setPageCount;
        }

        if (cachedState.pageCount && 'idpc' !== experience.WIZARD.pageCountModeStep.defaultMode) {
          restoredState.pageCount = cachedState.pageCount;
        }
      }
    }

    restoredState.settings = {
      ...restoredState.settings,
      addPicturesStep: payload.assistantSettings.addPicturesStep,
      cover1Step: payload.assistantSettings.cover1Step,
      cover2Step: payload.assistantSettings.backCoverStep,
      pageCountModeStep: payload.assistantSettings.pageCountModeStep,
    };

    dispatch(AssistantActions.setInit(restoredState));

    // Restore saved step.
    if (cachedState.step) {
      userFlowState.setCurrentStep(cachedState.step);
    }

    mounted.current = true;
  }, []);

  useEffect(() => {
    taopix.core.get('loader').on('onAssistantLoad', onAssistantLoadCallback);

    return () => {
      taopix.core.get('loader').off('onAssistantLoad', onAssistantLoadCallback);
    };
  }, []);

  useEffect(() => {
    // Only run for Smart Design in the Editor.
    if (!taopix.state.loaded || !mounted.current) return;
    if (Object.keys(productLayout || {}).length === 0) return;
    if (`#/${ROUTE_ASSISTANT}/` === settings.state.project.stage) return;
    if (
      AssistantType.SmartDesign !==
      experience.experience.WIZARD.designAssistantType.designAssistantType
    )
      return;

    const newState: Partial<AssistantState> = {};

    if (!mounted.current) {
      newState.useAllPictures =
        'uap' === experience.experience.WIZARD.picSelectionModeStep.defaultMode;
      newState.pageCount = getPageCount(productLayout.pagecount);
      newState.productLayout = productLayout;
      newState.originalPageCount = getPageCount(productLayout.pagecount);
    }

    const newSettings = deepClone(state.settings);

    newSettings.pageCountModeStep = {
      minPages: productLayout.minpages,
      maxPages: productLayout.maxpages,
      defaultPages: getPageCount(productLayout.defaultpages),
      step: productLayout.pageinsertcount,
    };

    dispatch(
      AssistantActions.setInit({
        ...newState,
        settings: newSettings,
      })
    );

    mounted.current = true;
  }, [taopix.state.loaded, mounted.current, productLayout?.code, productLayout?.pagecount]);

  useEffect(() => {
    // Set original page count at point of the Assistant opening.
    if (!taopix.state.loaded || !mounted.current) return;
    if (Object.keys(productLayout || {}).length === 0) return;
    if (
      AssistantType.SmartDesign !==
      experience.experience.WIZARD.designAssistantType.designAssistantType
    )
      return;

    dispatch(
      AssistantActions.setInit({
        originalPageCount: getPageCount(productLayout.pagecount),
      })
    );
  }, [userFlowState.isUserFlowActive(UserFlowType.Assistant)]);

  useEffect(() => {
    if (!taopix.state.loaded) return;
    if (!mounted.current) return;

    const data: Record<string, any> = Object.entries(state).reduce((accumulator, current) => {
      // Only save certain keys.
      if (
        ['coverAssets', 'coverItems', 'useAllPictures', 'setPageCount', 'pageCount'].includes(
          current[0]
        )
      ) {
        accumulator[current[0]] = current[1];
      }
      return accumulator;
    }, {});
    data.token = settings.state.tokens.token;
    data.step = currentStep;
    data.historyID = state.historyID;

    // Whenever the state changes, update the sessionstorage.
    sessionStorage.setItem('assistant', JSON.stringify(data));
  }, [
    taopix.state.loaded,
    state,
    state.coverAssets,
    state.coverItems,
    state.coverItems.front.pictureBox,
    state.coverItems.front.textBox,
    state.useAllPictures,
    state.setPageCount,
    state.pageCount,
    state.historyID,
    currentStep,
  ]);

  useEffect(() => {
    if (!taopix.state.loaded || !mounted.current) return;
    let newState = deepClone(state);

    const processCover = (coverMode: CoverMode): AssistantState => {
      const assistantSettings =
        CoverMode.Back === coverMode ? state.settings.cover2Step : state.settings.cover1Step;
      const experienceSettings =
        CoverMode.Back === coverMode
          ? experience.experience.WIZARD.backCoverStep
          : experience.experience.WIZARD.cover1Step;
      const cachedData = cachedState?.coverItems ? cachedState?.coverItems[coverMode] : null;
      const hasCover = experienceSettings.allowCustomersToSelectPicturesAndText;
      const pageNumber =
        CoverMode.Back === coverMode
          ? PageConstants.BackCoverPageNumber
          : PageConstants.FrontCoverPageNumber;

      if (hasCover) {
        let pageItems: {
          text: PageItem[];
          picture: Record<number, number>[];
        } = {
          picture: [],
          text: [],
        };

        if (window.location.hash.includes(ROUTE_ASSISTANT)) {
          // If we are in the fullscreen assistant then read from the assistant settings.
          pageItems.picture = assistantSettings.pictures.map(pictureRef => {
            return {[pictureRef]: -1};
          });

          if ((cachedData?.textBox?.length || 0) > 0) {
            pageItems.text = Object.values(assistantSettings.text).map(textBox => {
              return getCachedTextBox(cachedData?.textBox || [], textBox);
            });
          } else {
            pageItems.text = Object.values(assistantSettings.text);
          }
        } else {
          // Not in the assistant, read the page items from the cover.
          pageItems = sortPageItems(
            Object.values(pages.find(page => pageNumber === page.pagenumber)?.items || {})
          );
        }

        newState.coverItems[coverMode].hasCover =
          pageItems.picture.length > 0 || pageItems.text.length > 0;
        newState.coverItems[coverMode].pictureBox = pageItems.picture;
        newState.coverItems[coverMode].textBox = pageItems.text;
      } else {
        newState.coverItems[coverMode].hasCover = false;
        newState.coverItems[coverMode].pictureBox = [];
        newState.coverItems[coverMode].textBox = [];
      }

      return newState;
    };

    newState = processCover(CoverMode.Front);
    newState = processCover(CoverMode.Back);

    // Truncate cover assets in the event a picture box was removed.
    const coverAssets = {front: [...state.coverAssets.front], back: [...state.coverAssets.back]};
    if (state.coverAssets.front.length > newState.coverItems.front.pictureBox.length) {
      coverAssets.front = coverAssets.front.slice(0, newState.coverItems.front.pictureBox.length);
    }

    if (state.coverAssets.back.length > newState.coverItems.back.pictureBox.length) {
      coverAssets.back = coverAssets.back.slice(0, newState.coverItems.back.pictureBox.length);
    }

    dispatch(
      AssistantActions.setInit({
        coverItems: {...newState.coverItems},
        coverAssets: coverAssets,
      })
    );

    // Clear the cache data, we only restore it once.
    cachedState = {};
  }, [
    mounted.current,
    taopix.state.loaded,
    state.productLayout.code,
    window.location.hash.includes(ROUTE_ASSISTANT)
      ? false
      : taopix.core.get('stateManager')?.getPage(PageConstants.FrontCoverPageNumber)?.items,
    window.location.hash.includes(ROUTE_ASSISTANT)
      ? false
      : taopix.core.get('stateManager')?.getPage(PageConstants.BackCoverPageNumber)?.items,
  ]);

  useEffect(() => {
    if (!taopix.state.loaded) return;
    const currentState = deepClone(state);
    const newState = {
      coverAssets: currentState.coverAssets,
      selectedAssets: currentState.selectedAssets,
    };

    const filterCoverAssets = (coverMode: CoverMode) => {
      return newState.coverAssets[coverMode].filter(coverAssetId => {
        return assets.find(asset => coverAssetId === asset.id) ? true : false;
      });
    };

    // Check if selected assets have been removed.

    // Check front cover.
    newState.coverAssets[CoverMode.Front] = filterCoverAssets(CoverMode.Front);

    // Check back cover.
    newState.coverAssets[CoverMode.Back] = filterCoverAssets(CoverMode.Back);

    // Check selected assets.
    newState.selectedAssets = Object.values(
      newState.selectedAssets.filter(selectedAssetId => {
        return assets.find(asset => selectedAssetId === asset.id) ? true : false;
      })
    );

    dispatch(AssistantActions.setInit(newState));
  }, [taopix.state.loaded, assets.length]);

  useEffect(() => {
    if (!taopix.state.loaded) return;

    if (state.useAllPictures) {
      // Skip Smart Selection step if using all pictures.
      userFlowState.addSkipStep(
        UserFlowDirection.Both,
        `${UserFlowType.Assistant}.${UserFlowStep.AssistantSmartSelection}`
      );
    } else {
      userFlowState.removeSkipStep(
        UserFlowDirection.Both,
        `${UserFlowType.Assistant}.${UserFlowStep.AssistantSmartSelection}`
      );
    }
  }, [taopix.state.loaded, state.useAllPictures]);

  useEffect(() => {
    // Detect if any of the dependencies changed.
    if (!state.changeDetected) {
      dispatch(AssistantActions.setChangeDetected(true));
    }
  }, [
    assets.length,
    state.pageCount,
    state.useAllPictures,
    state.selectedAssets.length,
    state.coverItems,
  ]);

  useEffect(() => {
    if (!taopix.state.loaded) return;
    if (!userFlowState.isUserFlowActive(UserFlowType.Assistant)) return;

    let pageCount = state.pageCount;

    if (-1 === pageCount) {
      pageCount = productLayout.pagecount;
    }

    taopix.core.get('stateManager').updateLayout({
      pagecount: getPageCount(pageCount),
    });
  }, [state.pageCount]);

  useEffect(() => {
    if (!taopix.state.loaded) return;
    if (!window.location.hash.includes(ROUTE_ASSISTANT)) return;
    if (!userFlowState.isUserFlowActive(UserFlowType.Assistant)) return;
    // Only for Standard, Advanced and Smart Design assistant.
    if (
      ![AssistantType.Standard, AssistantType.Advanced, AssistantType.SmartDesign].includes(
        experience.experience.WIZARD.designAssistantType.designAssistantType
      )
    )
      return;
    if (0 === state.historyID) return;

    const steps = userFlowState.getSteps(UserFlowType.Assistant);
    const previewIndex = steps.findIndex(
      step => step === `${UserFlowType.Assistant}.${UserFlowStep.AssistantPreview}`
    );
    const newIndex = steps.findIndex(step => currentStep === step);
    const wentBack = newIndex > -1 && newIndex < previewIndex;

    if (wentBack) {
      // Remove the historyID early.
      const {historyID} = state;
      dispatch(AssistantActions.setHistoryID(0));
      dispatch(AssistantActions.setChangeDetected(true));

      taopix.core
        .get('stateManager')
        .call('undoredo', {
          type: 'undo',
          details: {
            data: {
              undoState: historyID,
            },
          },
          sessionRef: settings.state.tokens.session,
          showLoading: true,
        })
        .catch(error => {
          // Do nothing, but reset the history ID.
          dispatch(AssistantActions.setHistoryID(historyID));
        });
    }
  }, [currentStep]);

  const getCachedTextBox = (coverItems: PageItem[], item: PageItem): PageItem => {
    return coverItems.find((cachedItem: PageItem) => cachedItem.ref === item.ref) || item;
  };

  // const getCachedPictureBox = (coverItems: Record<number, number>[], itemRef: number): number => {
  //   const match = coverItems.find((cachedItem: Record<number, number>) => {
  //     const cachedItemRef = Object.values(cachedItem)[0];
  //     return cachedItemRef === itemRef;
  //   });

  //   return match ? Number(Object.values(match)[0]) : -1;
  // };

  const sortPageItems = (pageItems): {text: PageItem[]; picture: Record<number, number>[]} => {
    const clonedpageItems = deepClone(pageItems);
    const sortedPageItems = clonedpageItems.reduce(
      (accumulator, pageItem: PageItem) => {
        switch (true) {
          // Get picture boxes.
          case BoxType.Image === pageItem.type &&
            ![AssetType.ScrapbookImage, AssetType.ScrapbookClipping].includes(
              pageItem.hiresfiletype
            ) &&
            NumBool.True === pageItem.visible &&
            BoxLayer.Normal === pageItem.layer &&
            NumBool.False === pageItem.protectcontents:
            accumulator.picture.push({
              [Number(pageItem.ref)]: '' !== pageItem.hirespath ? Number(pageItem.hirespath) : -1,
            });
            break;
          // Get textboxes.
          case BoxType.Text === pageItem.type &&
            NumBool.True === pageItem.visible &&
            BoxLayer.Normal === pageItem.layer &&
            NumBool.False === pageItem.protectcontents:
            accumulator.text.push(pageItem);
            break;
        }

        return accumulator;
      },
      {text: [], picture: []}
    );

    return sortedPageItems;
  };

  const getPricingEngine = (aiMode: AIMode): PhotoBookPricingEngine => {
    return new (taopix.core.get('pricingEngine'))(
      currency,
      settings.state.price.pricing.quantity || 0,
      settings.state.price.pricing,
      AIMode.On === aiMode
    );
  };

  const formatCoverItems = (): any => {
    const coverItems: any = {
      front: {
        text: [],
        picture: [],
      },
      back: {
        text: [],
        picture: [],
      },
    };

    const formatAssets = (coverMode: CoverMode) => {
      const coverAssets = [...state.coverAssets[coverMode]];
      return state.coverItems[coverMode].pictureBox.map(pictureBox => {
        const pictureBoxRef = Object.keys(pictureBox)[0];
        let assetRef = pictureBox[pictureBoxRef];

        if (-1 === assetRef) {
          // No asset assigned, pick one from the selected cover items.
          assetRef = coverAssets.shift();
        }
        const asset = assets.find(asset => asset.id === assetRef);

        return {
          id: asset?.id || 0,
          hiresheight: asset?.height || 0,
          hireswidth: asset?.width || 0,
          rotation: asset?.rotation || 0,
          regions: asset?.metaData?.regions || [],
          itemref: Number(pictureBoxRef) || -999999,
        };
      });
    };

    const formatText = (coverMode: CoverMode) => {
      return state.coverItems[coverMode].textBox.map(textBox => {
        return {
          text: textBox.styledtext.data,
          autofitmode: textBox.styledtext.autofitmode,
          autofitscale: textBox.styledtext.autofitscale,
          itemref: textBox.ref,
        };
      });
    };

    // Format front cover items.
    if (experience.experience.WIZARD.cover1Step.allowCustomersToSelectPicturesAndText) {
      coverItems[CoverMode.Front].text = formatText(CoverMode.Front);
      coverItems[CoverMode.Front].picture = formatAssets(CoverMode.Front);
    }

    // Format back cover items.
    if (experience.experience.WIZARD.backCoverStep.allowCustomersToSelectPicturesAndText) {
      coverItems[CoverMode.Back].text = formatText(CoverMode.Back);
      coverItems[CoverMode.Back].picture = formatAssets(CoverMode.Back);
    }

    return coverItems;
  };

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      getPricingEngine,
      formatCoverItems,
      currency,
    }),
    [state, dispatch, settings.state.price.pricing, settings.state.price.pricing.quantity]
  );

  return <AssistantContext.Provider value={contextValue}>{children}</AssistantContext.Provider>;
};

const useAssistant = () => {
  const context = useContext(AssistantContext);
  if (undefined === context) {
    throw new Error('useAssistant must be used within a AssistantProvider');
  }
  return context;
};

export {AssistantProvider, useAssistant};
